/**
 * The ONE rule for turning a listing price into words.
 *
 * A price of **0 is a real, deliberate value meaning FREE**, not a missing one:
 * the package price field is `required(...)` and validated with
 * `noGreaterOrLessThanValidator(0, 9999)`
 * (src/forms/EditListingPricingForm/EditListingPricingPages/PackageComponent.js),
 * so an owner offering a free Reitbeteiligung has to type 0 on purpose. On real
 * production data that is **27% of listings** — free sharing is normal in this
 * market, and it is the single most attractive thing a post can say.
 *
 * Before this module the same fact was rendered three different ways:
 *   - the design badge hid it            (designDoc.formatPrice: `num <= 0` -> '')
 *   - the caption's price line said      "0 CHF"
 *   - the caption's package line said    "0 CHF pro Monat"
 * None of them said what 0 means, and the worst of them silently deleted the
 * best selling point on the post.
 *
 * What is shared here is the DECISION (unknown / free / an amount); each surface
 * keeps its own wording, because the badge reads "CHF 350" while a caption reads
 * "350 CHF pro Monat" and those orders are not ours to unify here.
 */

export const FREE_PRICE_LABEL = 'Gratis';

/**
 * Minor units (Rappen/cents, the Sharetribe Money convention) -> major units.
 *
 * Returns `null` when the price is genuinely UNKNOWN — absent, empty, or
 * unparseable — which is the case every caller should omit entirely. It returns
 * `0` for free, which callers must render as {@link FREE_PRICE_LABEL} rather
 * than as a number. Conflating those two is the bug this module exists to stop.
 *
 * A negative amount is not a price; it reads as unknown.
 *
 * @param {number|string|null|undefined} amount
 * @returns {number|null} major units, or null when unknown
 */
export const priceMajorUnits = amount => {
    if (amount == null || amount === '') return null;
    const major = Number(amount) / 100;
    return Number.isFinite(major) && major >= 0 ? major : null;
};

/** True when the price is known AND zero — i.e. deliberately free. */
export const isFreePrice = amount => priceMajorUnits(amount) === 0;

/**
 * The bare number as it should appear: no decimals for a whole amount (350),
 * two for a fractional one (350.50).
 * @param {number} major
 * @returns {string}
 */
export const priceNumberText = major =>
    Number.isInteger(major) ? String(major) : major.toFixed(2);
